import type { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';

function classes(...items: Array<string | false | null | undefined>) {
  return items.filter(Boolean).join(' ');
}

export function Panel({
  children,
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLElement>>) {
  return (
    <section className={classes('ui-panel', className)} {...props}>
      {children}
    </section>
  );
}

export function PanelHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow?: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <header className="ui-panel-header">
      <div>
        {eyebrow ? <span className="ui-eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
      </div>
      {action}
    </header>
  );
}

export function StatusPill({
  label,
  tone = 'neutral',
}: {
  label: string;
  tone?: 'cyan' | 'amber' | 'red' | 'green' | 'violet' | 'neutral';
}) {
  return <span className={`ui-status ui-status--${tone}`}>{label}</span>;
}

export function SyntheticBadge({ compact = false }: { compact?: boolean }) {
  return (
    <span className={classes('ui-synthetic', compact && 'ui-synthetic--compact')}>
      <span aria-hidden="true">◇</span> SYNTHETIC_DEMO
    </span>
  );
}

export function ProvenanceBadge({
  value,
  compact = false,
}: {
  value: 'LIVE_LOCAL' | 'SEEDED' | 'SYNTHETIC_DEMO' | 'AUTHORIZED_SEED';
  compact?: boolean;
}) {
  const label = value === 'AUTHORIZED_SEED' ? 'SEEDED' : value;
  return (
    <span
      className={classes(
        'ui-synthetic',
        `ui-provenance--${label.toLowerCase().replace('_', '-')}`,
        compact && 'ui-synthetic--compact',
      )}
    >
      <span aria-hidden="true">
        {label === 'LIVE_LOCAL' ? '●' : label === 'SEEDED' ? '◆' : '◇'}
      </span>{' '}
      {label}
    </span>
  );
}

export function PersonaAvatar({
  name,
  nature,
  status,
  compact = false,
}: {
  name: string;
  nature: 'HUMAN' | 'PERMANENT_AI' | 'AIZUBI';
  status: string;
  compact?: boolean;
}) {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('');
  return (
    <span
      className={classes(
        'ui-avatar',
        `ui-avatar--${status.toLowerCase()}`,
        compact && 'is-compact',
      )}
      aria-label={`${name}, ${nature === 'HUMAN' ? 'Mensch' : nature === 'AIZUBI' ? 'AiZubi' : 'KI-Persona'}, Status ${status}`}
    >
      {initials}
    </span>
  );
}

export function Metric({ label, value, note }: { label: string; value: ReactNode; note?: string }) {
  return (
    <div className="ui-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {note ? <small>{note}</small> : null}
    </div>
  );
}
